import { useQuery } from '@tanstack/react-query';
import React, { useState } from 'react';
import toast from 'react-hot-toast';
import {
    Users,
    Trash2,
    Mail,
    Stethoscope,
    UserPlus,
    AlertCircle,
    Loader2,
    X,
    AlertTriangle,
    Search,
    Filter
} from 'lucide-react';

// Confirmation Modal Component
const ConfirmationModal = ({ isOpen, onClose, onConfirm, doctor, isDeleting }) => {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
                <div className="p-6">
                    <div className="flex items-center gap-4 mb-4">
                        <div className="flex-shrink-0 w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                            <AlertTriangle className="h-5 w-5 text-red-600" />
                        </div>
                        <div className="flex-1">
                            <h3 className="text-lg font-semibold text-gray-900">Delete Doctor</h3>
                            <p className="text-sm text-gray-600 mt-1">
                                Are you sure you want to delete this doctor?
                            </p>
                        </div>
                        <button
                            onClick={onClose}
                            className="text-gray-400 hover:text-gray-600 transition-colors"
                        >
                            <X className="h-5 w-5" />
                        </button>
                    </div>

                    <div className="bg-gray-50 rounded-lg p-4 mb-6">
                        <div className="flex items-center gap-3">
                            <img
                                src={doctor?.image}
                                alt={doctor?.name}
                                className="w-12 h-12 rounded-full object-cover"
                            />
                            <div>
                                <p className="font-medium text-gray-900">{doctor?.name}</p>
                                <p className="text-sm text-gray-600">{doctor?.specialty}</p>
                            </div>
                        </div>
                    </div>

                    <p className="text-gray-700 mb-6">
                        If you delete <strong>{doctor?.name}</strong>, this action cannot be undone.
                        All associated data will be permanently removed.
                    </p>

                    <div className="flex gap-3">
                        <button
                            onClick={onClose}
                            disabled={isDeleting}
                            className="flex-1 bg-gray-200 hover:bg-gray-300 disabled:bg-gray-100 text-gray-700 px-4 py-2 rounded-lg font-medium transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={() => onConfirm(doctor)}
                            disabled={isDeleting}
                            className="flex-1 bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
                        >
                            {isDeleting ? (
                                <>
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                    Deleting...
                                </>
                            ) : (
                                <>
                                    <Trash2 className="h-4 w-4" />
                                    Delete
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

const ManageDoctor = () => {
    const [deletingDoctor, setDeletingDoctor] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterSpecialty, setFilterSpecialty] = useState('');

    const closeModal = () => {
        setDeletingDoctor(null);
        setIsDeleting(false);
    };

    const { data: doctors = [], isLoading, error, refetch } = useQuery({
        queryKey: ['doctors'],
        queryFn: async () => {
            try {
                const res = await fetch('https://doctors-portal-server-ten-iota.vercel.app/doctors', {
                    headers: {
                        authorization: `bearer ${localStorage.getItem('accessToken')}`
                    }
                });
                if (!res.ok) {
                    throw new Error('Failed to fetch doctors');
                }
                const data = await res.json();
                return data;
            } catch (error) {
                throw new Error('Error fetching doctors data');
            }
        }
    });

    const handleDeleteDoctor = async (doctor) => {
        setIsDeleting(true);
        try {
            const res = await fetch(`https://doctors-portal-server-ten-iota.vercel.app/doctors/${doctor._id}`, {
                method: 'DELETE',
                headers: {
                    authorization: `bearer ${localStorage.getItem('accessToken')}`
                }
            });

            if (!res.ok) {
                throw new Error('Failed to delete doctor');
            }

            const data = await res.json();
            if (data.deletedCount > 0) {
                refetch();
                toast.success(`Dr. ${doctor.name} has been deleted successfully`);
                closeModal();
            } else {
                throw new Error('No doctors deleted');
            }
        } catch (error) {
            console.error('Error deleting doctor:', error);
            toast.error('Failed to delete doctor. Please try again.');
        } finally {
            setIsDeleting(false);
        }
    };

    // Filter doctors based on search term and specialty
    const filteredDoctors = doctors.filter(doctor => {
        const matchesSearch = doctor.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            doctor.email.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesSpecialty = filterSpecialty === '' || doctor.specialty === filterSpecialty;
        return matchesSearch && matchesSpecialty;
    });

    // Get unique specialties for filter dropdown
    const uniqueSpecialties = [...new Set(doctors.map(doctor => doctor.specialty))];

    if (isLoading) {
        return (
            <div className="min-h-96 flex items-center justify-center">
                <div className="flex flex-col items-center gap-4">
                    <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                    <p className="text-gray-600">Loading doctors...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-96 flex items-center justify-center">
                <div className="flex flex-col items-center gap-4 text-center">
                    <AlertCircle className="h-12 w-12 text-red-500" />
                    <div>
                        <h3 className="text-lg font-semibold text-gray-900">Error Loading Doctors</h3>
                        <p className="text-gray-600 mt-1">Failed to fetch doctors data. Please try again.</p>
                    </div>
                    <button
                        onClick={() => refetch()}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
                    >
                        Try Again
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="p-6 bg-gray-50 min-h-screen">
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6 p-6">
                    <div className="flex items-center justify-between flex-wrap gap-4">
                        <div className="flex items-center gap-3">
                            <Users className="h-6 w-6 text-blue-600" />
                            <div>
                                <h1 className="text-2xl font-bold text-gray-900">Manage Doctors</h1>
                                <p className="text-gray-600">
                                    {filteredDoctors.length} of {doctors.length} doctors
                                    {searchTerm && ` matching "${searchTerm}"`}
                                    {filterSpecialty && ` in ${filterSpecialty}`}
                                </p>
                            </div>
                        </div>
                        <button className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors">
                            <UserPlus className="h-4 w-4" />
                            Add New Doctor
                        </button>
                    </div>

                    {/* Search and Filter */}
                    <div className="flex flex-col sm:flex-row gap-4 mt-6">
                        <div className="flex-1 relative">
                            <Search className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                            <input
                                type="text"
                                placeholder="Search doctors by name or email..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                        </div>
                        <div className="relative">
                            <Filter className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                            <select
                                value={filterSpecialty}
                                onChange={(e) => setFilterSpecialty(e.target.value)}
                                className="pl-10 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            >
                                <option value="">All Specialties</option>
                                {uniqueSpecialties.map(specialty => (
                                    <option key={specialty} value={specialty}>
                                        {specialty}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>

                {/* Doctors Table */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                    {filteredDoctors.length === 0 ? (
                        <div className="p-12 text-center">
                            <Users className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                            <h3 className="text-lg font-medium text-gray-900 mb-2">
                                {searchTerm || filterSpecialty ? 'No doctors found' : 'No doctors available'}
                            </h3>
                            <p className="text-gray-500">
                                {searchTerm || filterSpecialty
                                    ? 'Try adjusting your search or filter criteria'
                                    : 'Add a new doctor to get started'
                                }
                            </p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead>
                                    <tr className="bg-gray-50 border-b border-gray-200">
                                        <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            #
                                        </th>
                                        <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Doctor
                                        </th>
                                        <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Contact
                                        </th>
                                        <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Specialty
                                        </th>
                                        <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                    {filteredDoctors.map((doctor, i) => (
                                        <tr key={doctor._id} className="hover:bg-gray-50 transition-colors">
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {i + 1}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="flex items-center gap-4">
                                                    <div className="h-12 w-12 rounded-full overflow-hidden bg-gray-100">
                                                        <img
                                                            src={doctor.image}
                                                            alt={doctor.name}
                                                            className="h-full w-full object-cover"
                                                            onError={(e) => {
                                                                e.target.src = `https://ui-avatars.com/api/?name=${doctor.name}&background=3b82f6&color=fff`;
                                                            }}
                                                        />
                                                    </div>
                                                    <div>
                                                        <p className="text-sm font-medium text-gray-900">
                                                            Dr. {doctor.name}
                                                        </p>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="flex items-center gap-2">
                                                    <Mail className="h-4 w-4 text-gray-400" />
                                                    <span className="text-sm text-gray-900">{doctor.email}</span>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="flex items-center gap-2">
                                                    <Stethoscope className="h-4 w-4 text-gray-400" />
                                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                                        {doctor.specialty}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <button
                                                    onClick={() => setDeletingDoctor(doctor)}
                                                    className="inline-flex items-center gap-1 px-3 py-1 bg-red-600 hover:bg-red-700 text-white text-xs font-medium rounded-lg transition-colors"
                                                >
                                                    <Trash2 className="h-3 w-3" />
                                                    Delete
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {/* Confirmation Modal */}
            <ConfirmationModal
                isOpen={!!deletingDoctor}
                onClose={closeModal}
                onConfirm={handleDeleteDoctor}
                doctor={deletingDoctor}
                isDeleting={isDeleting}
            />
        </div>
    );
};

export default ManageDoctor;